// Consumes the boat/cloud "alarm enrichment is done" signal (change
// ftue-reveal-flow-cleanup, ④).
//
// The alarm pipeline already computes and BROADCASTS a definitive end: after the
// deterministic floor, the cloud runs the LLM enrichment and fires
// `alarm_analysis_ready` on the boat's `boat.<id>.devices` user-WS topic, carrying
// a terminal `status` (`ready` | `degraded` | `failed`) and the request id. The
// reveal never consumed it — so the "building" state cleared on the first
// (deterministic) batch and later LLM suggestions dribbled in silently. This hook
// surfaces that signal so the reveal can hold a "still working" state until
// enrichment truly settles, then settle once.
//
// Same topic + reconnect pattern as useSourceAnalysis / useBoatDevices (the
// notify rides that topic by design — see cloud gateway notifyAlarmAnalysisUpdate).

import { useEffect, useState } from 'react';
import { beforeWsReconnect } from './wsReconnect';

/** A terminal alarm-enrichment result, as broadcast on the devices topic. */
export interface AlarmAnalysisReady {
  /** `ready` (full LLM), `degraded` (deterministic-only), or `failed`. */
  status: string;
  /** Unique per analysis request — lets the consumer settle each event once. */
  requestId: string;
}

const BACKOFF_INITIAL_MS = 1_000;
const BACKOFF_MAX_MS = 16_000;

/**
 * Pure: is this WS frame an `alarm_analysis_ready` notification? Returns the
 * `{status, requestId}` when so, else null. Exported for unit tests.
 */
export function parseAlarmReadyFrame(raw: string): AlarmAnalysisReady | null {
  let frame: unknown;
  try {
    frame = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof frame !== 'object' || frame === null) return null;
  const f = frame as { type?: unknown; payload?: unknown };
  if (f.type !== 'devices' || typeof f.payload !== 'object' || f.payload === null) return null;
  const p = f.payload as { kind?: unknown; status?: unknown; requestId?: unknown };
  if (p.kind !== 'alarm_analysis_ready') return null;
  if (typeof p.status !== 'string' || typeof p.requestId !== 'string') return null;
  return { status: p.status, requestId: p.requestId };
}

/**
 * The latest `alarm_analysis_ready` seen for this boat, or null until one
 * arrives. `requestId` changes per analysis run, so a consumer can settle each
 * event exactly once (and settle again after a retry that runs a fresh analysis).
 */
export function useAlarmAnalysisReady(boatId: string): AlarmAnalysisReady | null {
  const [ready, setReady] = useState<AlarmAnalysisReady | null>(null);

  useEffect(() => {
    if (boatId === '' || typeof WebSocket === 'undefined') return;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffMs = BACKOFF_INITIAL_MS;
    let cancelled = false;

    function scheduleReconnect(): void {
      if (cancelled) return;
      const delay = backoffMs;
      backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
      reconnectTimer = setTimeout(connect, delay);
    }

    function connect(): void {
      if (cancelled) return;
      const url = new URL('/v1/ws', window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      let ws: WebSocket;
      try {
        ws = new WebSocket(url.toString());
      } catch {
        scheduleReconnect();
        return;
      }
      socket = ws;
      ws.onopen = () => {
        if (cancelled) {
          ws.close();
          return;
        }
        backoffMs = BACKOFF_INITIAL_MS;
        try {
          ws.send(JSON.stringify({ type: 'subscribe', topic: `boat.${boatId}.devices` }));
        } catch {
          // socket gone; onclose will retry
        }
      };
      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string') return;
        const parsed = parseAlarmReadyFrame(ev.data);
        if (parsed !== null) setReady(parsed);
      };
      ws.onclose = (ev) => {
        socket = null;
        // Renew before retrying when the cloud closed us for an expired access
        // token (task ws_session_expiry): reconnecting with the cookie it just
        // refused only earns another refusal, and the screen would sit stale
        // through the backoff. Reconnects either way.
        void beforeWsReconnect(ev.code).then(scheduleReconnect, scheduleReconnect);
      };
      ws.onerror = () => {
        // onclose handles reconnect.
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (socket !== null) {
        try {
          socket.close();
        } catch {
          /* noop */
        }
        socket = null;
      }
    };
  }, [boatId]);

  return ready;
}
